import React, { useState, useEffect, useCallback } from "react";
import { Plus, Trash2 } from "react-feather";
import { useTheme } from "../theme/ThemeContext";
import { useSettings, formatAmount } from "../settings/SettingsContext";
import { bankAccountsOf, paidFromFields } from "../utils/moneySources";
import MoneySourceSelect from "./MoneySourceSelect";
import { useMoneySources } from "./useMoneySources";

import { API_BASE, apiFetch } from "../api";

const emptyLoanForm = { name: "", monthlyPayment: "", remainingBalance: "" };
const emptyAssetForm = { name: "", type: "Bank Account", value: "" };
const ASSET_TYPES = ["Bank Account", "Investment", "Other"];

const NetWorthCard = () => {
    const [mode, setMode] = useState("own"); // "own" or "owe"
    const { tokens, fonts } = useTheme();
    const { settings } = useSettings();
    const masked = settings.maskAmounts;

    const [loans, setLoans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(emptyLoanForm);
    const [formError, setFormError] = useState(null);
    const [saving, setSaving] = useState(false);

    const [assets, setAssets] = useState([]);
    const [loadingAssets, setLoadingAssets] = useState(true);
    const [errorAssets, setErrorAssets] = useState(false);
    const [showAssetForm, setShowAssetForm] = useState(false);
    const [assetForm, setAssetForm] = useState(emptyAssetForm);
    const [assetFormError, setAssetFormError] = useState(null);
    const [savingAsset, setSavingAsset] = useState(false);
    const [editingAssetId, setEditingAssetId] = useState(null);
    const [editAssetValue, setEditAssetValue] = useState("");

    const loadLoans = useCallback(() => {
        setLoading(true);
        setError(false);
        apiFetch(`${API_BASE}/loans`)
            .then((res) => (res.ok ? res.json() : Promise.reject()))
            .then(setLoans)
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, []);

    const loadAssets = useCallback(() => {
        setLoadingAssets(true);
        setErrorAssets(false);
        apiFetch(`${API_BASE}/assets`)
            .then((res) => (res.ok ? res.json() : Promise.reject()))
            .then(setAssets)
            .catch(() => setErrorAssets(true))
            .finally(() => setLoadingAssets(false));
    }, []);

    useEffect(() => {
        loadLoans();
        loadAssets();
    }, [loadLoans, loadAssets]);

    const totalOwed = loans.reduce((sum, loan) => sum + loan.remainingBalance, 0);
    const totalOwned = assets.reduce((sum, asset) => sum + asset.value, 0);

    const startEditAsset = (asset) => {
        setEditingAssetId(asset.id);
        setEditAssetValue(String(asset.value));
    };

    const saveAssetValue = (asset) => {
        const value = parseFloat(editAssetValue);
        if (Number.isNaN(value) || (value < 0 && asset.category !== "account")) return;
        setAssets((prev) => prev.map((a) => (a.id === asset.id ? { ...a, value } : a)));
        setEditingAssetId(null);
        apiFetch(`${API_BASE}/assets/${asset.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ value }),
        }).catch(() => loadAssets());
    };

    const handleDeleteAsset = (id) => {
        const previous = assets;
        setAssets((prev) => prev.filter((a) => a.id !== id));
        apiFetch(`${API_BASE}/assets/${id}`, { method: "DELETE" }).catch(() => setAssets(previous));
    };

    const handleAssetFormSubmit = (e) => {
        e.preventDefault();
        setAssetFormError(null);

        // An investment's current value is optional; a bank account needs its balance.
        const blank = String(assetForm.value).trim() === "";
        const value = blank && assetForm.type !== "Bank Account" ? null : parseFloat(assetForm.value);
        if (!assetForm.name.trim()) return setAssetFormError("Name is required.");
        // A bank account can be below zero (overdrawn); an investment can't.
        if (value !== null && (Number.isNaN(value) || (value < 0 && assetForm.type !== "Bank Account"))) return setAssetFormError("Value must be a valid number.");
        const last4 = assetForm.type === "Bank Account" ? (assetForm.last4 || "").trim() : "";
        if (last4 && !/^\d{4}$/.test(last4)) return setAssetFormError("Last 4 digits must be 4 numbers, or leave it empty.");

        setSavingAsset(true);
        apiFetch(`${API_BASE}/assets`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: assetForm.name, type: assetForm.type, value, last4: last4 || null }),
        })
            .then((res) => (res.ok ? res.json() : res.json().then((data) => Promise.reject(new Error(data.error)))))
            .then((created) => {
                setAssets((prev) => [...prev, created]);
                setAssetForm(emptyAssetForm);
                setShowAssetForm(false);
            })
            .catch((err) => setAssetFormError(err.message || "Failed to add asset."))
            .finally(() => setSavingAsset(false));
    };

    // Records one EMI on the server: the oldest unpaid one is marked paid (or one is
    // added for today). Interest comes out first and the rest off the loan, the same as
    // marking the EMI paid anywhere else; unmarking or deleting it in Transactions undoes it.
    const [payNote, setPayNote] = useState(null);
    const handleLogPayment = (loan) => {
        setPayNote(null);
        apiFetch(`${API_BASE}/loans/${loan.id}/pay`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" })
            .then(async (res) => {
                const data = await res.json().catch(() => ({}));
                if (!res.ok) return setPayNote({ error: true, text: data.error || "Couldn't record the payment." });
                const paidOff = loan.remainingBalance - data.loan.remainingBalance;
                setPayNote({ text: `${loan.name}: EMI of ${formatAmount(data.bill.amount, masked)} recorded (dated ${data.bill.dueDate}); ${formatAmount(Math.round(paidOff), masked)} came off the loan, the rest was interest.` });
            })
            .finally(loadLoans);
    };

    const handleDeleteLoan = (id) => {
        const previous = loans;
        setLoans((prev) => prev.filter((l) => l.id !== id));
        apiFetch(`${API_BASE}/loans/${id}`, { method: "DELETE" }).catch(() => setLoans(previous));
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        setFormError(null);

        const monthlyPayment = parseFloat(form.monthlyPayment);
        const remainingBalance = parseFloat(form.remainingBalance);
        if (!form.name.trim()) return setFormError("Name is required.");
        if (Number.isNaN(monthlyPayment) || monthlyPayment < 0) return setFormError("Monthly payment must be a valid number.");
        if (Number.isNaN(remainingBalance) || remainingBalance < 0) return setFormError("Remaining balance must be a valid number.");

        setSaving(true);
        apiFetch(`${API_BASE}/loans`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: form.name, monthlyPayment, remainingBalance }),
        })
            .then((res) => (res.ok ? res.json() : res.json().then((data) => Promise.reject(new Error(data.error)))))
            .then((created) => {
                setLoans((prev) => [...prev, created]);
                setForm(emptyLoanForm);
                setShowForm(false);
            })
            .catch((err) => setFormError(err.message || "Failed to add loan."))
            .finally(() => setSaving(false));
    };

    return (
        <div style={{ ...cardStyle(tokens), flexDirection: "column", position: "relative" }}>
            <div style={cardRowStyle(tokens)}>
                <p style={{ ...styles.cardTitle, color: tokens.textPrimary, fontFamily: fonts.display }}>My Net Worth</p>
                <div>
                    <button
                        onClick={() => setMode("own")}
                        style={{
                            ...styles.toggleButton,
                            backgroundColor: mode === "own" ? tokens.accent : "transparent",
                            color: mode === "own" ? tokens.accentContrast : tokens.accent,
                            borderColor: tokens.accent,
                            marginRight: 8,
                        }}
                    >
                        I Own
                    </button>
                    <button
                        onClick={() => setMode("owe")}
                        style={{
                            ...styles.toggleButton,
                            backgroundColor: mode === "owe" ? tokens.negative : "transparent",
                            color: mode === "owe" ? "#fff" : tokens.negative,
                            borderColor: tokens.negative,
                        }}
                    >
                        I Owe
                    </button>
                </div>
            </div>

            <div style={{ marginTop: 12, width: "100%" }}>
                {mode === "own" ? (
                    <>
                        {loadingAssets && <p style={{ ...styles.subItem, color: tokens.textMuted }}>Loading...</p>}
                        {errorAssets && <p style={{ ...styles.subItem, color: tokens.negative }}>Couldn't reach the backend.</p>}
                        {!loadingAssets && !errorAssets && (
                            <>
                                <p style={{ ...styles.cardValue, fontFamily: fonts.mono, color: tokens.textPrimary }}>
                                    {formatAmount(totalOwned, masked)}
                                </p>
                                {assets.length === 0 && (
                                    <p style={{ ...styles.subItem, color: tokens.textMuted }}>No assets tracked yet.</p>
                                )}
                                {assets.map((asset) => (
                                    <div
                                        key={asset.id}
                                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}
                                    >
                                        {editingAssetId === asset.id ? (
                                            <div style={{ display: "flex", alignItems: "center", gap: 6, width: "100%" }}>
                                                <span style={{ ...styles.subItem, color: tokens.textMuted, flexShrink: 0 }}>{asset.name}:</span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={editAssetValue}
                                                    onChange={(e) => setEditAssetValue(e.target.value)}
                                                    style={{ ...styles.loanInput, width: 100, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                                                />
                                                <button onClick={() => saveAssetValue(asset)} style={{ ...styles.saveLoanBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}>
                                                    Save
                                                </button>
                                                <button onClick={() => setEditingAssetId(null)} style={{ ...styles.smallActionBtn, backgroundColor: "transparent", color: tokens.textMuted }}>
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <span style={{ ...styles.subItem, color: tokens.textMuted }}>
                                                    {asset.name}: {formatAmount(asset.value, masked)}
                                                    <span style={{ color: tokens.textMuted, opacity: 0.7 }}> ({asset.type})</span>
                                                </span>
                                                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                                                    <button
                                                        onClick={() => startEditAsset(asset)}
                                                        style={{ ...styles.smallActionBtn, backgroundColor: tokens.neutralSoft, color: tokens.textPrimary }}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteAsset(asset.id)}
                                                        aria-label={`Delete ${asset.name}`}
                                                        style={{ ...styles.iconBtnSmall, color: tokens.negative }}
                                                    >
                                                        <Trash2 size={13} />
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}

                                {!showAssetForm ? (
                                    <button
                                        onClick={() => setShowAssetForm(true)}
                                        style={{ ...styles.addLoanBtn, color: tokens.accent }}
                                    >
                                        <Plus size={13} strokeWidth={3} /> Add Asset
                                    </button>
                                ) : (
                                    <form onSubmit={handleAssetFormSubmit} style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                                        <input
                                            placeholder="Asset name"
                                            value={assetForm.name}
                                            onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })}
                                            style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                                        />
                                        <select
                                            value={assetForm.type}
                                            onChange={(e) => setAssetForm({ ...assetForm, type: e.target.value })}
                                            style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                                        >
                                            {ASSET_TYPES.map((t) => (
                                                <option key={t} value={t}>{t}</option>
                                            ))}
                                        </select>
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder={assetForm.type === "Bank Account" ? "Value" : "Value (optional)"}
                                            value={assetForm.value}
                                            onChange={(e) => setAssetForm({ ...assetForm, value: e.target.value })}
                                            style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                                        />
                                        {assetForm.type === "Bank Account" && (
                                            <input
                                                inputMode="numeric"
                                                maxLength={4}
                                                placeholder="Last 4 digits of account (optional, matches bank SMS)"
                                                value={assetForm.last4 || ""}
                                                onChange={(e) => setAssetForm({ ...assetForm, last4: e.target.value })}
                                                style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                                            />
                                        )}
                                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                            <button
                                                type="submit"
                                                disabled={savingAsset}
                                                style={{ ...styles.saveLoanBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}
                                            >
                                                {savingAsset ? "Saving..." : "Save"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => { setShowAssetForm(false); setAssetFormError(null); }}
                                                style={{ ...styles.saveLoanBtn, backgroundColor: "transparent", color: tokens.textMuted }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                        {assetFormError && <span style={{ fontSize: 12, color: tokens.negative }}>{assetFormError}</span>}
                                    </form>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    <>
                        {loading && <p style={{ ...styles.subItem, color: tokens.textMuted }}>Loading...</p>}
                        {error && <p style={{ ...styles.subItem, color: tokens.negative }}>Couldn't reach the backend.</p>}
                        {!loading && !error && (
                            <>
                                <p style={{ ...styles.cardValue, fontFamily: fonts.mono, color: tokens.textPrimary }}>
                                    {formatAmount(totalOwed, masked)}
                                </p>
                                {loans.length === 0 && (
                                    <p style={{ ...styles.subItem, color: tokens.textMuted }}>No loans tracked yet.</p>
                                )}
                                {loans.map((loan) => (
                                    <div
                                        key={loan.id}
                                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}
                                    >
                                        <span style={{ ...styles.subItem, color: tokens.textMuted }}>
                                            {loan.name}: {formatAmount(loan.remainingBalance, masked)}
                                            <span style={{ color: tokens.textMuted, opacity: 0.7 }}> ({formatAmount(loan.monthlyPayment, masked)}/mo)</span>
                                        </span>
                                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                                            <button
                                                onClick={() => handleLogPayment(loan)}
                                                disabled={loan.remainingBalance <= 0}
                                                style={{ ...styles.smallActionBtn, backgroundColor: tokens.neutralSoft, color: tokens.textPrimary }}
                                            >
                                                Log Payment
                                            </button>
                                            <button
                                                onClick={() => handleDeleteLoan(loan.id)}
                                                aria-label={`Delete ${loan.name}`}
                                                style={{ ...styles.iconBtnSmall, color: tokens.negative }}
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {payNote && (
                                    <p role="status" style={{ fontSize: 12, margin: "4px 0 8px", color: payNote.error ? tokens.negative : tokens.positive }}>{payNote.text}</p>
                                )}

                                {!showForm ? (
                                    <button
                                        onClick={() => setShowForm(true)}
                                        style={{ ...styles.addLoanBtn, color: tokens.accent }}
                                    >
                                        <Plus size={13} strokeWidth={3} /> Add Loan
                                    </button>
                                ) : (
                                    <form onSubmit={handleFormSubmit} style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                                        <input
                                            placeholder="Loan name"
                                            value={form.name}
                                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                                        />
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="Monthly payment"
                                            value={form.monthlyPayment}
                                            onChange={(e) => setForm({ ...form, monthlyPayment: e.target.value })}
                                            style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                                        />
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="Remaining balance"
                                            value={form.remainingBalance}
                                            onChange={(e) => setForm({ ...form, remainingBalance: e.target.value })}
                                            style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                                        />
                                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                            <button
                                                type="submit"
                                                disabled={saving}
                                                style={{ ...styles.saveLoanBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}
                                            >
                                                {saving ? "Saving..." : "Save"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => { setShowForm(false); setFormError(null); }}
                                                style={{ ...styles.saveLoanBtn, backgroundColor: "transparent", color: tokens.textMuted }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                        {formError && <span style={{ fontSize: 12, color: tokens.negative }}>{formError}</span>}
                                    </form>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
            <div style={{ ...footerRowStyle(tokens), marginTop: 4 }}>
                <span style={{ color: tokens.textMuted, fontSize: 12 }}>
                    {mode === "owe" ? "Tracked in your loans" : "Tracked in your assets"}
                </span>
                <div />
            </div>

        </div>
    );
};

const emptyCardForm = { name: "", cardLimit: "", balance: "", billingDay: "", last4: "" };

const CreditCardsCard = () => {
    const { tokens, fonts } = useTheme();
    const { settings } = useSettings();
    const masked = settings.maskAmounts;

    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(emptyCardForm);
    const [formError, setFormError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editBalance, setEditBalance] = useState("");
    // Paying a card's bill from a bank account: { id, amount, from }.
    const [paying, setPaying] = useState(null);
    const [payError, setPayError] = useState(null);
    const sources = useMoneySources();

    const loadCards = useCallback(() => {
        setLoading(true);
        setError(false);
        apiFetch(`${API_BASE}/credit-cards`)
            .then((res) => (res.ok ? res.json() : Promise.reject()))
            .then(setCards)
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        loadCards();
    }, [loadCards]);

    const totalLimit = cards.reduce((sum, c) => sum + c.cardLimit, 0);
    const totalRemaining = cards.reduce((sum, c) => sum + (c.cardLimit - c.balance), 0);

    const startEditBalance = (card) => {
        setEditingId(card.id);
        setEditBalance(String(card.balance));
    };

    const saveBalance = (card) => {
        const balance = parseFloat(editBalance);
        if (Number.isNaN(balance) || balance < 0) return;
        setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, balance } : c)));
        setEditingId(null);
        apiFetch(`${API_BASE}/credit-cards/${card.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ balance }),
        }).catch(() => loadCards());
    };

    // Lowers what's owed on the card and the account's balance; kept as a "Card
    // payment" in Transactions, where deleting it undoes both.
    const savePayment = () => {
        const amount = parseFloat(paying.amount);
        if (Number.isNaN(amount) || amount <= 0) return setPayError("Enter how much you paid.");
        if (bankAccountsOf(sources.assets).length > 0 && !paying.from) return setPayError("Choose the account you paid from.");
        setPayError(null);
        apiFetch(`${API_BASE}/credit-cards/${paying.id}/pay`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ amount, fromAccountId: paidFromFields(paying.from).paidFromId }),
        })
            .then((res) => (res.ok ? setPaying(null) : res.json().then((data) => setPayError(data.error || "Couldn't save that."))))
            .finally(loadCards);
    };

    const handleDeleteCard = (id) => {
        const previous = cards;
        setCards((prev) => prev.filter((c) => c.id !== id));
        apiFetch(`${API_BASE}/credit-cards/${id}`, { method: "DELETE" }).catch(() => setCards(previous));
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        setFormError(null);

        const cardLimit = parseFloat(form.cardLimit);
        const balance = parseFloat(form.balance || 0);
        const billingDay = parseInt(form.billingDay, 10);
        if (!form.name.trim()) return setFormError("Name is required.");
        if (Number.isNaN(cardLimit) || cardLimit < 0) return setFormError("Limit must be a valid number.");
        if (Number.isNaN(balance) || balance < 0) return setFormError("Balance must be a valid number.");
        if (!Number.isInteger(billingDay) || billingDay < 1 || billingDay > 31) return setFormError("Billing day must be 1-31.");
        const last4 = (form.last4 || "").trim();
        if (last4 && !/^\d{4}$/.test(last4)) return setFormError("Last 4 digits must be 4 numbers, or leave it empty.");

        setSaving(true);
        apiFetch(`${API_BASE}/credit-cards`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: form.name, cardLimit, balance, billingDay, last4: last4 || null }),
        })
            .then((res) => (res.ok ? res.json() : res.json().then((data) => Promise.reject(new Error(data.error)))))
            .then((created) => {
                setCards((prev) => [...prev, created]);
                setForm(emptyCardForm);
                setShowForm(false);
            })
            .catch((err) => setFormError(err.message || "Failed to add card."))
            .finally(() => setSaving(false));
    };

    return (
        <div style={cardStyle(tokens)}>
            <div style={cardRowStyle(tokens)}>
                <p style={{ ...styles.cardTitle, color: tokens.textPrimary, fontFamily: fonts.display }}>Credit Cards</p>
            </div>

            <div style={{ width: "100%" }}>
                {loading && <p style={{ ...styles.subItem, color: tokens.textMuted }}>Loading...</p>}
                {error && <p style={{ ...styles.subItem, color: tokens.negative }}>Couldn't reach the backend.</p>}
                {!loading && !error && cards.length === 0 && (
                    <p style={{ ...styles.subItem, color: tokens.textMuted }}>No cards tracked yet.</p>
                )}
                {!loading && !error && cards.map((card) => (
                    <div
                        key={card.id}
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 12,
                            gap: 8,
                        }}
                    >
                        <div>
                            <p style={{ fontSize: 16, margin: 0, fontWeight: "700", color: tokens.textPrimary }}>
                                {card.name}{card.last4 ? ` ••${card.last4}` : ""}
                            </p>
                            <p style={{ fontSize: 12, margin: "2px 0 0 0", color: tokens.textMuted }}>
                                Owed {formatAmount(card.balance, masked)}
                            </p>
                            <p style={{ fontSize: 12, margin: "2px 0 0 0", color: tokens.textMuted }}>
                                Bill on {card.billingDay}
                                {card.billingDay === 1 ? "st" : card.billingDay === 2 ? "nd" : card.billingDay === 3 ? "rd" : "th"} of every month
                            </p>
                        </div>

                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                            {editingId === card.id ? (
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={editBalance}
                                        onChange={(e) => setEditBalance(e.target.value)}
                                        style={{ ...styles.loanInput, width: 90, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                                    />
                                    <button onClick={() => saveBalance(card)} style={{ ...styles.saveLoanBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}>
                                        Save
                                    </button>
                                    <button onClick={() => setEditingId(null)} style={{ ...styles.smallActionBtn, backgroundColor: "transparent", color: tokens.textMuted }}>
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <p style={{ fontWeight: "700", fontSize: 16, margin: 0, fontFamily: fonts.mono, color: tokens.textPrimary }}>
                                        {formatAmount(card.cardLimit, masked)}
                                    </p>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                                        <p style={{ fontSize: 12, color: tokens.textMuted, margin: "2px 0 0 0" }}>
                                            remaining {formatAmount(card.cardLimit - card.balance, masked)}
                                        </p>
                                        <button onClick={() => startEditBalance(card)} style={{ ...styles.smallActionBtn, backgroundColor: tokens.neutralSoft, color: tokens.textPrimary }}>
                                            Edit
                                        </button>
                                        {card.balance > 0 && (
                                            <button onClick={() => { setPayError(null); setPaying({ id: card.id, amount: String(card.balance), from: "" }); }} style={{ ...styles.smallActionBtn, backgroundColor: tokens.positiveBg, color: tokens.positive }}>
                                                Pay bill
                                            </button>
                                        )}
                                        <button onClick={() => handleDeleteCard(card.id)} aria-label={`Delete ${card.name}`} style={{ ...styles.iconBtnSmall, color: tokens.negative }}>
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                ))}

                {paying && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, margin: "4px 0 12px", padding: 10, borderRadius: 10, backgroundColor: tokens.neutralSoft }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: tokens.textPrimary }}>
                            Pay {(cards.find((c) => c.id === paying.id) || {}).name} bill
                        </span>
                        <input
                            type="number"
                            step="0.01"
                            placeholder="Amount paid"
                            value={paying.amount}
                            onChange={(e) => setPaying({ ...paying, amount: e.target.value })}
                            style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                        />
                        {bankAccountsOf(sources.assets).length > 0 && (
                            <MoneySourceSelect
                                style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                                value={paying.from}
                                onChange={(v) => setPaying({ ...paying, from: v })}
                                assets={sources.assets}
                                accountsOnly
                                required
                            />
                        )}
                        <div style={{ display: "flex", gap: 8 }}>
                            <button onClick={savePayment} style={{ ...styles.saveLoanBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}>Save payment</button>
                            <button onClick={() => setPaying(null)} style={{ ...styles.saveLoanBtn, backgroundColor: "transparent", color: tokens.textMuted }}>Cancel</button>
                        </div>
                        {payError && <span style={{ fontSize: 12, color: tokens.negative }}>{payError}</span>}
                    </div>
                )}

                {!showForm ? (
                    <button onClick={() => setShowForm(true)} style={{ ...styles.addLoanBtn, color: tokens.accent }}>
                        <Plus size={13} strokeWidth={3} /> Add Card
                    </button>
                ) : (
                    <form onSubmit={handleFormSubmit} style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                        <input
                            placeholder="Card name"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                        />
                        <input
                            type="number"
                            step="0.01"
                            placeholder="Credit limit"
                            value={form.cardLimit}
                            onChange={(e) => setForm({ ...form, cardLimit: e.target.value })}
                            style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                        />
                        <input
                            type="number"
                            step="0.01"
                            placeholder="Current balance (used)"
                            value={form.balance}
                            onChange={(e) => setForm({ ...form, balance: e.target.value })}
                            style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                        />
                        <input
                            type="number"
                            min="1"
                            max="31"
                            placeholder="Billing day (1-31)"
                            value={form.billingDay}
                            onChange={(e) => setForm({ ...form, billingDay: e.target.value })}
                            style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                        />
                        <input
                            inputMode="numeric"
                            maxLength={4}
                            placeholder="Last 4 digits (optional, matches bank SMS)"
                            value={form.last4 || ""}
                            onChange={(e) => setForm({ ...form, last4: e.target.value })}
                            style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                        />
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <button type="submit" disabled={saving} style={{ ...styles.saveLoanBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}>
                                {saving ? "Saving..." : "Save"}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setShowForm(false); setFormError(null); }}
                                style={{ ...styles.saveLoanBtn, backgroundColor: "transparent", color: tokens.textMuted }}
                            >
                                Cancel
                            </button>
                        </div>
                        {formError && <span style={{ fontSize: 12, color: tokens.negative }}>{formError}</span>}
                    </form>
                )}
            </div>

            <div style={{ ...footerRowStyle(tokens), marginTop: 4 }}>
                <span style={{ color: tokens.textMuted, fontSize: 12 }}>Tracked in your cards</span>

                {cards.length > 0 && (
                    <div style={{ color: tokens.textMuted }}>
                        <span style={{ fontWeight: "700", color: tokens.textPrimary }}>
                            {formatAmount(totalLimit, masked)}
                        </span>{" "}
                        | remaining {formatAmount(totalRemaining, masked)}
                    </div>
                )}
            </div>
        </div>
    );
};

const emptyPlanForm = { name: "", targetDate: "", budget: "", collected: "" };

const formatPlanDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const BudgetPlanCard = () => {
  const [filter, setFilter] = useState("Coming Up");
  const { tokens, fonts } = useTheme();
  const { settings } = useSettings();
  const masked = settings.maskAmounts;

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyPlanForm);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editCollected, setEditCollected] = useState("");

  const loadPlans = useCallback(() => {
    setLoading(true);
    setError(false);
    apiFetch(`${API_BASE}/budget-plans`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then(setPlans)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const plansWithStatus = plans.map((p) => ({ ...p, status: p.collected >= p.budget ? "Completed" : "Coming Up" }));
  const filteredPlans = plansWithStatus.filter((p) => p.status === filter);

  const startEditCollected = (plan) => {
    setEditingId(plan.id);
    setEditCollected(String(plan.collected));
  };

  const saveCollected = (plan) => {
    const collected = parseFloat(editCollected);
    if (Number.isNaN(collected) || collected < 0) return;
    setPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, collected } : p)));
    setEditingId(null);
    apiFetch(`${API_BASE}/budget-plans/${plan.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ collected }),
    }).catch(() => loadPlans());
  };

  const handleDeletePlan = (id) => {
    const previous = plans;
    setPlans((prev) => prev.filter((p) => p.id !== id));
    apiFetch(`${API_BASE}/budget-plans/${id}`, { method: "DELETE" }).catch(() => setPlans(previous));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormError(null);

    const budget = parseFloat(form.budget);
    const collected = parseFloat(form.collected || 0);
    if (!form.name.trim()) return setFormError("Name is required.");
    if (Number.isNaN(budget) || budget < 0) return setFormError("Budget must be a valid number.");
    if (Number.isNaN(collected) || collected < 0) return setFormError("Collected must be a valid number.");

    setSaving(true);
    apiFetch(`${API_BASE}/budget-plans`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: form.name, targetDate: form.targetDate || null, budget, collected }),
    })
      .then((res) => (res.ok ? res.json() : res.json().then((data) => Promise.reject(new Error(data.error)))))
      .then((created) => {
        setPlans((prev) => [...prev, created]);
        setForm(emptyPlanForm);
        setShowForm(false);
      })
      .catch((err) => setFormError(err.message || "Failed to add plan."))
      .finally(() => setSaving(false));
  };

  return (
    <div style={cardStyle(tokens)}>
      <div style={cardRowStyle(tokens)}>
        <p style={{ ...styles.cardTitle, color: tokens.textPrimary, fontFamily: fonts.display }}>Budget Plan</p>
        <div style={{ display: "flex", gap: 8 }}>
          {["Coming Up", "Completed"].map((option) => (
            <button
              key={option}
              style={{
                ...styles.toggleButton,
                borderColor: filter === option ? tokens.accent : tokens.surfaceBorder,
                color: filter === option ? tokens.accent : tokens.textMuted,
              }}
              onClick={() => setFilter(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      <div style={{ width: "100%" }}>
        {loading && <p style={{ ...styles.subItem, color: tokens.textMuted }}>Loading...</p>}
        {error && <p style={{ ...styles.subItem, color: tokens.negative }}>Couldn't reach the backend.</p>}
        {!loading && !error && filteredPlans.length === 0 && (
          <p style={{ ...styles.subItem, color: tokens.textMuted }}>No {filter.toLowerCase()} plans.</p>
        )}
        {!loading && !error && filteredPlans.slice(0, 4).map((plan) => (
          <div key={plan.id} style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <div>
              <p style={{ ...styles.subItem, fontWeight: 600, marginBottom: 4, color: tokens.textPrimary }}>
                {plan.name}
              </p>
              <p style={{ fontSize: 12, color: tokens.textMuted, margin: 0 }}>
                {plan.targetDate ? formatPlanDate(plan.targetDate) : "No date set"}
              </p>
            </div>

            {editingId === plan.id ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="number"
                  step="0.01"
                  value={editCollected}
                  onChange={(e) => setEditCollected(e.target.value)}
                  style={{ ...styles.loanInput, width: 90, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
                />
                <button onClick={() => saveCollected(plan)} style={{ ...styles.saveLoanBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}>
                  Save
                </button>
                <button onClick={() => setEditingId(null)} style={{ ...styles.smallActionBtn, backgroundColor: "transparent", color: tokens.textMuted }}>
                  Cancel
                </button>
              </div>
            ) : (
              <div style={{ textAlign: "right" }}>
                <p style={{ ...styles.subItem, fontWeight: 600, marginBottom: 4, color: tokens.textPrimary }}>
                  {formatAmount(plan.budget, masked)}
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                  <span style={{ fontSize: 12, color: tokens.textMuted }}>Collected: {formatAmount(plan.collected, masked)}</span>
                  <button onClick={() => startEditCollected(plan)} style={{ ...styles.smallActionBtn, backgroundColor: tokens.neutralSoft, color: tokens.textPrimary }}>
                    Edit
                  </button>
                  <button onClick={() => handleDeletePlan(plan.id)} aria-label={`Delete ${plan.name}`} style={{ ...styles.iconBtnSmall, color: tokens.negative }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* How much of the goal is saved so far. */}
          <div style={{ height: 8, borderRadius: 4, marginTop: 8, backgroundColor: tokens.neutralSoft, overflow: "hidden" }}>
            <div style={{ width: `${plan.budget > 0 ? Math.min(100, (plan.collected / plan.budget) * 100) : 100}%`, height: "100%", backgroundColor: plan.collected >= plan.budget ? tokens.positive : tokens.accent }} />
          </div>
          <p style={{ fontSize: 11, color: tokens.textMuted, margin: "4px 0 0" }}>
            {plan.budget > 0 ? Math.min(100, Math.round((plan.collected / plan.budget) * 100)) : 100}% saved
          </p>
          </div>
        ))}

        {!showForm ? (
          <button onClick={() => setShowForm(true)} style={{ ...styles.addLoanBtn, color: tokens.accent }}>
            <Plus size={13} strokeWidth={3} /> Add Plan
          </button>
        ) : (
          <form onSubmit={handleFormSubmit} style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            <input
              placeholder="Plan name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
            />
            <input
              type="date"
              value={form.targetDate}
              onChange={(e) => setForm({ ...form, targetDate: e.target.value })}
              style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
            />
            <input
              type="number"
              step="0.01"
              placeholder="Budget goal"
              value={form.budget}
              onChange={(e) => setForm({ ...form, budget: e.target.value })}
              style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
            />
            <input
              type="number"
              step="0.01"
              placeholder="Already collected (optional)"
              value={form.collected}
              onChange={(e) => setForm({ ...form, collected: e.target.value })}
              style={{ ...styles.loanInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
            />
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button type="submit" disabled={saving} style={{ ...styles.saveLoanBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}>
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setFormError(null); }}
                style={{ ...styles.saveLoanBtn, backgroundColor: "transparent", color: tokens.textMuted }}
              >
                Cancel
              </button>
            </div>
            {formError && <span style={{ fontSize: 12, color: tokens.negative }}>{formError}</span>}
          </form>
        )}
      </div>
      <div style={{ ...footerRowStyle(tokens), marginTop: 4 }}>
        <span style={{ color: tokens.textMuted, fontSize: 12 }}>Tracked in your budget plans</span>
        <div />
      </div>

    </div>
  );
};
export { NetWorthCard, CreditCardsCard, BudgetPlanCard };

const cardStyle = (tokens) => ({
    backgroundColor: tokens.surface,
    border: `1px solid ${tokens.surfaceBorder}`,
    boxShadow: tokens.cardShadow,
    padding: 20,
    borderRadius: 16,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 16,
    transition: "background-color 0.2s ease, border-color 0.2s ease",
});

const cardRowStyle = (tokens) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    borderBottom: `1px solid ${tokens.surfaceBorder}`,
    paddingBottom: 8,
});

const footerRowStyle = (tokens) => ({
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    borderTop: `1px solid ${tokens.surfaceBorder}`,
    paddingTop: 12,
    fontSize: 12,
});


const styles = {
    cardTitle: {
        fontSize: 15.5,
        fontWeight: "700",
        margin: 0,
    },
    cardValue: {
        fontSize: 24,
        fontWeight: "700",
        margin: "8px 0 0 0",
    },
    subItem: {
        fontSize: 14,
        margin: "4px 0",
    },
    toggleButton: {
        border: "1.5px solid",
        borderRadius: "6px",
        padding: "6px 12px",
        cursor: "pointer",
        fontWeight: "600",
        fontSize: "14px",
        outline: "none",
        background: "transparent",
    },
    viewAllBtn: {
        background: "transparent",
        border: "none",
        fontWeight: "600",
        cursor: "pointer",
        padding: 0,
    },
    smallActionBtn: {
        border: "none",
        borderRadius: 6,
        padding: "3px 8px",
        fontSize: 11,
        fontWeight: 700,
        cursor: "pointer",
    },
    iconBtnSmall: {
        background: "transparent",
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        padding: 2,
    },
    addLoanBtn: {
        display: "flex",
        alignItems: "center",
        gap: 4,
        background: "transparent",
        border: "none",
        fontWeight: "700",
        fontSize: 12.5,
        cursor: "pointer",
        padding: 0,
        marginTop: 6,
    },
    loanInput: {
        padding: "6px 10px",
        borderRadius: 6,
        border: "1px solid",
        fontSize: 13,
    },
    saveLoanBtn: {
        border: "none",
        borderRadius: 6,
        padding: "6px 14px",
        fontWeight: "700",
        fontSize: 12.5,
        cursor: "pointer",
    },
};
